import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Folder,
  FolderOpen,
  Loader2,
  Package,
  FolderKanban,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useDownloadFile, useMediaTree } from "../hooks/useMedia";
import {
  mediaNeedsAttention,
  type MediaFile,
  type MediaTreeNode,
  type MediaTreePhaseGroup,
} from "../types";
import { FileTypeIcon } from "./FileTypeIcon";

type Props = {
  rootEntityType?: "COMPANY" | "PRODUCT" | "PROJECT";
  rootEntityId?: string;
};

export function DocumentTreeView({ rootEntityType, rootEntityId }: Props) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading } = useMediaTree({
    rootEntityType,
    rootEntityId,
    search: search.trim() || undefined,
  });

  const downloadMutation = useDownloadFile();
  const nodes = data?.nodes ?? [];

  // When a search is active, auto-expand any branch with matches.
  const autoExpand = useMemo(() => {
    if (!search.trim()) return null;
    const ids = new Set<string>();
    function walk(n: MediaTreeNode): boolean {
      const ownMatch = n.documents.length > 0;
      let childMatch = false;
      for (const c of n.children) {
        if (walk(c)) childMatch = true;
      }
      const phaseMatch = (n.phaseGroups ?? []).some(
        (g) =>
          g.documents.length > 0 ||
          g.stages.some((s) => s.documents.length > 0),
      );
      const has = ownMatch || childMatch || phaseMatch;
      if (has) ids.add(nodeKey(n));
      return has;
    }
    nodes.forEach(walk);
    return ids;
  }, [nodes, search]);

  function isExpanded(n: MediaTreeNode): boolean {
    if (autoExpand) return autoExpand.has(nodeKey(n));
    return expanded.has(nodeKey(n));
  }

  function toggle(n: MediaTreeNode) {
    if (autoExpand) return;
    setExpanded((s) => {
      const next = new Set(s);
      const k = nodeKey(n);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  return (
    <div
      className="rounded-xl border bg-white"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      {/* Filter bar */}
      <div
        className="flex items-center gap-2 border-b px-3 py-2"
        style={{ borderColor: "hsl(210,30%,90%)" }}
      >
        <div className="relative flex-1 max-w-[340px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder="Search documents in tree..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8"
          />
        </div>
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="h-8 px-2 inline-flex items-center gap-1 rounded-md border bg-white text-xs text-muted-foreground hover:bg-muted"
          >
            <X size={12} strokeWidth={1.5} /> Clear
          </button>
        )}
      </div>

      {/* Body */}
      <div className="p-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
            Loading tree…
          </div>
        ) : nodes.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No accessible entities to display.
          </div>
        ) : (
          <ul className="space-y-1">
            {nodes.map((n) => (
              <TreeNode
                key={nodeKey(n)}
                node={n}
                depth={0}
                isExpanded={isExpanded}
                onToggle={toggle}
                onOpenFile={(f) => navigate(`/media-vault/${f.id}`)}
                onDownloadFile={(f) =>
                  downloadMutation.mutate({
                    id: f.id,
                    originalName: f.originalName,
                    isExternal: f.isExternal,
                    externalUrl: f.externalUrl,
                  })
                }
                searchActive={!!search.trim()}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function nodeKey(n: MediaTreeNode): string {
  return `${n.entity.type}:${n.entity.id}`;
}

function totalDocCount(n: MediaTreeNode): number {
  let count = n.documents.length;
  for (const c of n.children) count += totalDocCount(c);
  for (const g of n.phaseGroups ?? []) {
    count += g.documents.length;
    for (const s of g.stages) count += s.documents.length;
  }
  return count;
}

function TreeNode({
  node,
  depth,
  isExpanded,
  onToggle,
  onOpenFile,
  onDownloadFile,
  searchActive,
}: {
  node: MediaTreeNode;
  depth: number;
  isExpanded: (n: MediaTreeNode) => boolean;
  onToggle: (n: MediaTreeNode) => void;
  onOpenFile: (f: MediaFile) => void;
  onDownloadFile: (f: MediaFile) => void;
  searchActive: boolean;
}) {
  const docCount = totalDocCount(node);
  const isEmpty = docCount === 0 && node.children.length === 0;
  const open = isExpanded(node);
  const EntityIcon = entityIconFor(node.entity.type);

  // Empty branches collapse-by-default and dim.
  const dimmed = isEmpty;

  return (
    <li>
      <div
        className={cn(
          "flex items-center gap-1.5 rounded px-1.5 py-1 cursor-pointer hover:bg-muted/50",
          dimmed && "opacity-50",
        )}
        style={{ paddingLeft: `${depth * 14 + 4}px` }}
        onClick={() => onToggle(node)}
      >
        <button
          type="button"
          className="h-4 w-4 flex items-center justify-center text-muted-foreground"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node);
          }}
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? (
            <ChevronDown size={12} strokeWidth={1.5} />
          ) : (
            <ChevronRight size={12} strokeWidth={1.5} />
          )}
        </button>
        {open ? (
          <FolderOpen size={14} strokeWidth={1.5} className="text-amber-600" />
        ) : (
          <Folder size={14} strokeWidth={1.5} className="text-amber-600" />
        )}
        <EntityIcon size={11} strokeWidth={1.5} className="text-muted-foreground" />
        <span className="text-[13px] font-medium text-foreground">
          {node.entity.name}
        </span>
        {node.entity.referenceId && (
          <span className="text-[10px] font-mono text-muted-foreground">
            ({node.entity.referenceId})
          </span>
        )}
        <span className="text-[10px] text-muted-foreground ml-1">
          {docCount} doc{docCount === 1 ? "" : "s"}
        </span>
      </div>

      {open && (
        <div>
          {/* Direct documents on this entity */}
          {node.documents.length > 0 && (
            <ul
              className="space-y-0.5 mt-0.5"
              style={{ paddingLeft: `${depth * 14 + 28}px` }}
            >
              {node.documents.map((d) => (
                <DocumentRow
                  key={d.id}
                  doc={d}
                  onOpen={onOpenFile}
                  onDownload={onDownloadFile}
                />
              ))}
              {node.documentsTruncated && (
                <li className="text-[11px] text-muted-foreground italic">
                  Showing first 100 — open in Media Vault for the full list.
                </li>
              )}
            </ul>
          )}

          {/* Phase groups (PROJECT only) */}
          {node.phaseGroups && node.phaseGroups.length > 0 && (
            <div
              className="mt-1 space-y-1"
              style={{ paddingLeft: `${depth * 14 + 18}px` }}
            >
              {node.phaseGroups.map((g) => (
                <PhaseGroupRow
                  key={g.phase.id}
                  group={g}
                  onOpen={onOpenFile}
                  onDownload={onDownloadFile}
                />
              ))}
            </div>
          )}

          {/* Recurse into children */}
          {node.children.length > 0 && (
            <ul className="mt-0.5 space-y-1">
              {node.children.map((c) => (
                <TreeNode
                  key={nodeKey(c)}
                  node={c}
                  depth={depth + 1}
                  isExpanded={isExpanded}
                  onToggle={onToggle}
                  onOpenFile={onOpenFile}
                  onDownloadFile={onDownloadFile}
                  searchActive={searchActive}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function PhaseGroupRow({
  group,
  onOpen,
  onDownload,
}: {
  group: MediaTreePhaseGroup;
  onOpen: (f: MediaFile) => void;
  onDownload: (f: MediaFile) => void;
}) {
  const [open, setOpen] = useState(true);
  const total =
    group.documents.length +
    group.stages.reduce((s, st) => s + st.documents.length, 0);
  if (total === 0) return null;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground"
      >
        {open ? (
          <ChevronDown size={11} strokeWidth={1.5} />
        ) : (
          <ChevronRight size={11} strokeWidth={1.5} />
        )}
        <Folder size={11} strokeWidth={1.5} className="text-violet-600" />
        Phase: {group.phase.name}
        <span className="text-[10px]">({total})</span>
      </button>
      {open && (
        <div className="ml-4 mt-1 space-y-1">
          {group.documents.length > 0 && (
            <ul className="space-y-0.5">
              {group.documents.map((d) => (
                <DocumentRow
                  key={d.id}
                  doc={d}
                  onOpen={onOpen}
                  onDownload={onDownload}
                />
              ))}
            </ul>
          )}
          {group.stages.map((s) =>
            s.documents.length > 0 ? (
              <div key={s.stage.id}>
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Folder
                    size={10}
                    strokeWidth={1.5}
                    className="text-teal-600"
                  />
                  Stage: {s.stage.name}
                  <span className="text-[10px]">({s.documents.length})</span>
                </div>
                <ul className="ml-4 space-y-0.5 mt-0.5">
                  {s.documents.map((d) => (
                    <DocumentRow
                      key={d.id}
                      doc={d}
                      onOpen={onOpen}
                      onDownload={onDownload}
                    />
                  ))}
                </ul>
              </div>
            ) : null,
          )}
        </div>
      )}
    </div>
  );
}

function DocumentRow({
  doc,
  onOpen,
  onDownload,
}: {
  doc: MediaFile;
  onOpen: (f: MediaFile) => void;
  onDownload: (f: MediaFile) => void;
}) {
  return (
    <li className="group flex items-center gap-2 text-[12px] hover:bg-muted/40 rounded px-1 py-0.5">
      <FileTypeIcon fileType={doc.fileType} size={14} />
      {mediaNeedsAttention(doc) && (
        <span
          className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600"
          title="Missing details — click to add"
          aria-label="Missing details"
        >
          <Sparkles size={8} strokeWidth={2} />
        </span>
      )}
      <button
        type="button"
        onClick={() => onOpen(doc)}
        className="flex-1 text-left truncate hover:text-primary"
      >
        {doc.title}
      </button>
      <span className="text-[10px] text-muted-foreground">
        {doc.uploadedByName ?? "—"}
      </span>
      <button
        type="button"
        onClick={() => onDownload(doc)}
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted"
        aria-label="Download"
      >
        {doc.isExternal ? (
          <ExternalLink size={12} strokeWidth={1.5} />
        ) : (
          <Download size={12} strokeWidth={1.5} />
        )}
      </button>
    </li>
  );
}

function entityIconFor(t: "COMPANY" | "PRODUCT" | "PROJECT") {
  if (t === "COMPANY") return Building2;
  if (t === "PRODUCT") return Package;
  return FolderKanban;
}
